// Catalog-driven storefront data — Phase 1, Part 4.
//
// One place that loads the website's products and categories from the CRM
// catalog (GET /api/catalog/products + /api/catalog/categories) and turns them
// into what the Shop, category pages, menu and home page show.
//
// The 18 hand-built product pages still use their own content (mock.js) until
// they move to the catalog in Phase 3, but whether they are shown at all, and
// in which category, now comes from the catalog. If the catalog can't be
// reached, the site falls back to the hand-built list so it never goes blank.
import { useEffect, useState } from "react";
import { PRODUCTS, NAV_CATEGORIES, TOP_CATEGORIES } from "../mock";

const BASE = process.env.REACT_APP_BACKEND_URL;

// Photo URLs from the catalog API are server paths (/api/catalog/photos/...).
// Prefix them with the backend address; leave full URLs and site assets alone.
export function mediaUrl(u) {
  if (!u || typeof u !== "string") return null;
  if (/^(https?:|data:|blob:)/.test(u)) return u;
  return u.startsWith("/api/") ? `${BASE}${u}` : u;
}

let cached = null; // { status: "ready" | "error", products, categories }
let inflight = null;
const listeners = new Set();

export function loadStorefront(force = false) {
  if (cached && !force) return Promise.resolve(cached);
  if (inflight) return inflight;
  const get = (path) =>
    fetch(`${BASE}${path}`, { credentials: "include" }).then((r) => {
      if (!r.ok) throw new Error(`${path} ${r.status}`);
      return r.json();
    });
  inflight = Promise.all([get("/api/catalog/products"), get("/api/catalog/categories")])
    .then(([p, c]) => ({ status: "ready", products: p.products || [], categories: c.categories || [] }))
    .catch(() => ({ status: "error", products: [], categories: [] }))
    .then((data) => {
      cached = data;
      inflight = null;
      listeners.forEach((fn) => fn(data));
      return data;
    });
  return inflight;
}

export function useStorefront() {
  const [data, setData] = useState(cached || { status: "loading", products: [], categories: [] });
  useEffect(() => {
    listeners.add(setData);
    if (!cached) loadStorefront();
    else setData(cached);
    return () => listeners.delete(setData);
  }, []);
  return data;
}

const MOCK_BY_SLUG = Object.fromEntries(PRODUCTS.map((p) => [p.slug, p]));
export const isHandBuilt = (slug) => !!MOCK_BY_SLUG[slug];

export const formatMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// Is this product switched off on the website? Only answerable once the
// catalog has loaded; on error we never hide anything.
export function isHiddenOnSite(slug, data) {
  if (!data || data.status !== "ready") return false;
  return !data.products.some((p) => p.slug === slug);
}

// Everything a product card needs, from either source.
function cardFromMock(p, catalogRow) {
  return {
    slug: p.slug,
    name: p.name,
    image: p.image || null,
    tagline: p.tagline || "",
    description: p.description || "",
    features: p.features || [],
    fromCents: typeof p.fromPrice === "number" ? Math.round(p.fromPrice * 100) : null,
    minQty: p.quantityTiers ? p.quantityTiers[0]?.qty : p.minQty,
    category: (catalogRow && catalogRow.category) || p.category,
    handBuilt: true,
  };
}

function cardFromCatalog(p) {
  return {
    slug: p.slug,
    name: p.name,
    image: mediaUrl(p.image || (p.images || [])[0]),
    tagline: p.tagline || "",
    description: p.description || "",
    features: p.features || [],
    fromCents: p.from_price_cents,
    minQty: null,
    category: p.category,
    handBuilt: false,
  };
}

// All products the website shows, in catalog order. Catalog products without a
// price yet are left out: they can't be bought until they are priced.
export function storefrontProducts(data) {
  if (!data || data.status !== "ready") {
    return data && data.status === "loading" ? [] : PRODUCTS.map((p) => cardFromMock(p, null));
  }
  const out = [];
  for (const row of data.products) {
    const mock = MOCK_BY_SLUG[row.slug];
    if (mock) out.push(cardFromMock(mock, row));
    else if (row.from_price_cents) out.push(cardFromCatalog(row));
  }
  return out;
}

// Website categories (DB). Falls back to the old fixed list if the catalog
// can't be reached.
export function storefrontCategories(data) {
  if (data && data.status === "ready" && data.categories.length) return data.categories;
  if (data && data.status === "loading") return [];
  const nav = Object.fromEntries(NAV_CATEGORIES.map((c) => [c.slug, c]));
  return TOP_CATEGORIES.map((c, i) => ({
    slug: c.slug,
    name: c.name,
    menu_label: nav[c.slug]?.label || c.name,
    page_title: c.name,
    eyebrow: "",
    description: "",
    image: c.image,
    order: i,
    menu_order: nav[c.slug] ? NAV_CATEGORIES.indexOf(nav[c.slug]) : 999,
    show_in_menu: !!nav[c.slug],
    show_on_home: true,
    menu_items: [],
    menu_sections: [],
    extra_links: [],
  }));
}

export function categoryImage(cat) {
  if (!cat || !cat.image) return null;
  return cat.image.startsWith("http") ? cat.image : `${BASE}${cat.image}`;
}

// Menu entries in the shape the Header renders: {slug, label, items | sections}.
// Curated labels/groups come from the category; any other visible product in
// the category is added automatically, so a new product shows up in the menu.
export function navCategories(data) {
  if (!data || data.status !== "ready") return NAV_CATEGORIES; // loading or unreachable: the old menu
  const products = storefrontProducts(data);
  const visible = new Set(products.map((p) => p.slug));
  const byCat = {};
  products.forEach((p) => { (byCat[p.category] = byCat[p.category] || []).push(p); });
  return storefrontCategories(data)
    .filter((c) => c.show_in_menu)
    .sort((a, b) => (a.menu_order ?? 0) - (b.menu_order ?? 0))
    .map((c) => {
      const listed = new Set();
      const pick = (items) => (items || []).filter((it) => it.slug && visible.has(it.slug)).map((it) => {
        listed.add(it.slug);
        return { label: it.label, slug: it.slug };
      });
      const links = (c.extra_links || []).map((l) => ({ label: l.label, to: l.to }));
      if ((c.menu_sections || []).length) {
        const sections = c.menu_sections
          .map((s) => ({ title: s.title, items: pick(s.items) }))
          .filter((s) => s.items.length);
        const more = (byCat[c.slug] || []).filter((p) => !listed.has(p.slug)).map((p) => ({ label: p.name, slug: p.slug }));
        if (more.length) sections.push({ title: "More", items: more });
        if (links.length) sections.push({ title: "Services", items: links });
        const items = sections.flatMap((s) => s.items);
        return { slug: c.slug, label: c.menu_label || c.name, sections: sections.length ? sections : undefined, items };
      }
      const items = pick(c.menu_items);
      (byCat[c.slug] || []).forEach((p) => { if (!listed.has(p.slug)) items.push({ label: p.name, slug: p.slug }); });
      return { slug: c.slug, label: c.menu_label || c.name, items: [...items, ...links] };
    })
    .filter((c) => c.sections || c.items.length); // a new, still-empty category stays out of the menu
}
